import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Spinner from "../../Components/Spinner";
import { ToastComponent } from "../../Components/ToastComponent";
import { clientService } from "../../../Services/client.service";
import { administrationService } from "../../../Services/administration.service";
import { representativesService } from "../../../Services/representatives.service";
import { errorHandling } from "../../../Services/error.handling";
import { formatPhoneNumber, formatToE164 } from "../../../Services/string.service";
import { getCompanyIdCookie } from "../../../Utils/companyIdCookie";
import SearchInputDocument from "../../Components/Forms/SearchInputDocument";
const CLIENT_TYPE = { PF: "PF", PJ: "PJ" };
const YES_NO = { YES: "S", NO: "N" };

// Campos <input disabled> (não os desabilitados temporariamente via
// isLoadingClient) renderizam alguns px mais altos que um .form-control
// normal — quirk de navegador, não achamos regra de CSS causando isso.
// Fixando a altura aqui, só nesses inputs, em vez de mexer em
// .form-control:disabled (compartilhado com o app inteiro).
const DISABLED_FIELD_HEIGHT = { height: "calc(1.53em + 0.875rem + 2px)" };

// Aba "Dados Pessoais / Administrativos" do cliente (issue #453, dados reais
// via GET /Customers/{id} na issue #490). Tipo de Restrição, Representante e
// Comercial são alimentados por endpoints próprios (administration.service.js
// e representatives.service.js) — Comercial é sub-recurso do Representante
// selecionado (GET /representatives/{id}/sales), então recarrega em cascata.
//
// clientId / isNewCustomer / onClientSaved vêm de CustomerSearch (issue
// #499). POST /customers e PUT /customers/{id} confirmados via swagger —
// nomes de campo do payload ajustados pra bater exatamente com o schema
// (GET usa nomes diferentes do POST/PUT, ex: ClientName -> CompanyName).
//
// PENDENTE: RegionalId, MasterUserEmail, MasterUserName e
// MasterUserAccessLevelId são obrigatórios no swagger (POST e PUT), mas não
// existe state/input pra eles na tela nem eles vêm no GET do cliente.
// Mandados como null por enquanto — confirmar com backend/Reginaldo de onde
// tirar esses valores antes de considerar o payload fechado.
//
// Layout: cada bloco (Dados do Cliente, Financeiro, Restrição Administrativa,
// Responsável Comercial, Plano de Serviço) é um "card" Bootstrap separado —
// mesmo componente que CustomerSearch.jsx usa como wrapper da tela toda —
// pra herdar o tema real do app em vez de estilo inventado. Campos
// somente-leitura (ID Cliente, Data Cadastro, Última Alteração, Última
// Suspensão) ficam disabled com um texto de apoio, sem tentar simular
// "modo leitura" com classes fora do padrão do resto do sistema.
//
// Nome e Doc (CPF/CNPJ) são obrigatórios pra salvar (cadastro e edição) —
// valida antes de chamar a API, marca os campos inválidos e rola a tela
// pro topo pra garantir que o toast de erro seja visto mesmo em formulário
// grande e rolado pra baixo.
export default function PersonalData({
  clientId,
  isNewCustomer,
  onClientSaved,
  mainAddress,
  billingAddress,
}) {
  const { t } = useTranslation();

  const [clientName, setClientName] = useState("");
  const [clientTradeName, setClientTradeName] = useState("");
  const [doc, setDoc] = useState("");
  const [stateRegistration, setStateRegistration] = useState("");
  const [municipalRegistration, setMunicipalRegistration] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [phone1, setPhone1] = useState("");
  const [phone2, setPhone2] = useState("");
  const [responsibleEmail, setResponsibleEmail] = useState("");
  const [createdAt, setCreatedAt] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");

  const [clientType, setClientType] = useState("");
  const isPF = clientType === CLIENT_TYPE.PF;
  const [isSimpleTax, setIsSimpleTax] = useState(false);
  const [creditAnalysis, setCreditAnalysis] = useState("");
  const [dda, setDda] = useState("");
  const [installmentBooklet, setInstallmentBooklet] = useState("");
  const [servicePlan, setServicePlan] = useState("");
  const [restrictionType, setRestrictionType] = useState("");
  const [restrictionStartDate, setRestrictionStartDate] = useState("");
  const [restrictionEndDate, setRestrictionEndDate] = useState("");
  const [lastSuspensionDate, setLastSuspensionDate] = useState("");
  const [representative, setRepresentative] = useState("");
  const [salesperson, setSalesperson] = useState("");

  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [clientError, setClientError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toastParams, setToastParams] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [plans, setPlans] = useState([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [plansError, setPlansError] = useState(false);

  const [restrictions, setRestrictions] = useState([]);
  const [isLoadingRestrictions, setIsLoadingRestrictions] = useState(false);
  const [restrictionsError, setRestrictionsError] = useState(false);

  const [representatives, setRepresentatives] = useState([]);
  const [isLoadingRepresentatives, setIsLoadingRepresentatives] = useState(false);
  const [representativesError, setRepresentativesError] = useState(false);

  const [salespeople, setSalespeople] = useState([]);
  const [isLoadingSalespeople, setIsLoadingSalespeople] = useState(false);
  const [salespeopleError, setSalespeopleError] = useState(false);

  const showToast = (title, body, color) => {
    setToastParams(JSON.stringify({ show: true, title, body, color }));
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Busca os dados do cliente selecionado. Sem clientId ou em fluxo de
  // Novo Cliente, limpa os campos em vez de buscar.
  useEffect(() => {
    let isMounted = true;

    if (!clientId || isNewCustomer) {
      setClientName("");
      setClientTradeName("");
      setDoc("");
      setStateRegistration("");
      setMunicipalRegistration("");
      setBirthDate("");
      setPhone1("");
      setPhone2("");
      setResponsibleEmail("");
      setCreatedAt("");
      setUpdatedAt("");

      setClientType("");
      setIsSimpleTax(false);
      setCreditAnalysis("");
      setDda("");
      setInstallmentBooklet("");
      setServicePlan("");
      setRestrictionType("");
      setRestrictionStartDate("");
      setRestrictionEndDate("");
      setLastSuspensionDate("");
      setRepresentative("");
      setSalesperson("");
      setFieldErrors({});
      return undefined;
    }

    const loadClient = async () => {
      setIsLoadingClient(true);
      setClientError(false);
      try {
        const response = await clientService.fetchClient(clientId);
        if (!isMounted) return;

        if (!response || response.error) {
          setClientError(true);
          return;
        }

        setClientName(response.clientName ?? "");
        setClientTradeName(response.clientTradeName ?? "");
        setDoc(response.doc ?? "");
        setStateRegistration(response.stateRegistration ?? "");
        setMunicipalRegistration(response.municipalRegistration ?? "");
        setBirthDate(response.birthDate ?? "");
        setPhone1(response.phone1 ?? "");
        setPhone2(response.phone2 ?? "");
        setResponsibleEmail(response.responsibleEmail ?? "");
        setCreatedAt(response.createdAt ?? "");
        setUpdatedAt(response.updatedAt ?? "");

        setClientType(response.personType ?? "");
        setIsSimpleTax(Boolean(response.simpleOption));
        setCreditAnalysis(response.creditAnalysis ?? "");
        setDda(response.dda ?? "");
        setInstallmentBooklet(response.paymentBook ?? "");
        setServicePlan(response.servicePlanId ?? "");
        setRestrictionType(response.administrativeRestrictionId ?? "");
        setRestrictionStartDate(response.administrativeRestrictionDate ?? "");
        setRestrictionEndDate(response.administrativeRestrictionEndDate ?? "");
        setLastSuspensionDate(response.lastTemporarySuspensionDate ?? "");
        setRepresentative(response.representativeId ?? "");
        setSalesperson(response.salespersonId ?? "");
      } catch (error) {
        if (isMounted) setClientError(true);
      } finally {
        if (isMounted) setIsLoadingClient(false);
      }
    };

    loadClient();
    return () => {
      isMounted = false;
    };
  }, [clientId, isNewCustomer]);

  useEffect(() => {
    let isMounted = true;

    const loadPlans = async () => {
      setIsLoadingPlans(true);
      setPlansError(false);
      try {
        const response = await clientService.fetchPlans();
        if (isMounted) setPlans(Array.isArray(response) ? response : []);
      } catch (error) {
        if (isMounted) setPlansError(true);
      } finally {
        if (isMounted) setIsLoadingPlans(false);
      }
    };

    loadPlans();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadRestrictions = async () => {
      setIsLoadingRestrictions(true);
      setRestrictionsError(false);
      try {
        const response = await administrationService.fetchRestrictions();
        if (isMounted) setRestrictions(Array.isArray(response) ? response : []);
      } catch (error) {
        if (isMounted) setRestrictionsError(true);
      } finally {
        if (isMounted) setIsLoadingRestrictions(false);
      }
    };

    loadRestrictions();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadRepresentatives = async () => {
      setIsLoadingRepresentatives(true);
      setRepresentativesError(false);
      try {
        const response = await representativesService.fetchRepresentatives();
        if (isMounted) setRepresentatives(Array.isArray(response) ? response : []);
      } catch (error) {
        if (isMounted) setRepresentativesError(true);
      } finally {
        if (isMounted) setIsLoadingRepresentatives(false);
      }
    };

    loadRepresentatives();
    return () => {
      isMounted = false;
    };
  }, []);

  // Comercial é sub-recurso do Representante selecionado — recarrega sempre
  // que o representante mudar (inclusive na carga inicial vinda do fetch do
  // cliente). O reset da seleção de Comercial fica em
  // handleRepresentativeChange, não aqui, pra não apagar o salesperson vindo
  // do fetch do cliente.
  useEffect(() => {
    let isMounted = true;

    if (!representative) {
      setSalespeople([]);
      return undefined;
    }

    const loadSalespeople = async () => {
      setIsLoadingSalespeople(true);
      setSalespeopleError(false);
      try {
        const response = await representativesService.fetchSalespeople(representative);
        if (isMounted) setSalespeople(Array.isArray(response) ? response : []);
      } catch (error) {
        if (isMounted) setSalespeopleError(true);
      } finally {
        if (isMounted) setIsLoadingSalespeople(false);
      }
    };

    loadSalespeople();
    return () => {
      isMounted = false;
    };
  }, [representative]);

  const handleRepresentativeChange = (value) => {
    setRepresentative(value);
    setSalesperson("");
  };

  // Valida os campos obrigatórios (Nome, CPF/CNPJ, Representante, Plano de
  // Serviço) antes de salvar. Retorna true se está tudo ok; caso contrário
  // marca os campos inválidos, mostra o toast e rola a tela pro topo pro
  // usuário ver o erro.
  const validateRequiredFields = () => {
    const errors = {};

    if (!clientName.trim()) {
      errors.clientName = true;
    }
    if (!doc.trim()) {
      errors.doc = true;
    }
    if (!representative) {
      errors.representative = true;
    }
    if (!servicePlan) {
      errors.servicePlan = true;
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      scrollToTop();
      showToast(
        t("COMMON.ATTENTION"),
        t("CUSTOMER.PERSONAL_DATA.REQUIRED_FIELDS_ERROR"),
        "Warning"
      );
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateRequiredFields()) {
      return;
    }

    setIsSaving(true);

    // Nomes de campo confirmados via swagger (POST /api/v3/customers e
    // PUT /api/v3/customers/{clientId}). RegionalId/MasterUser* ainda sem
    // origem confirmada na tela — mandados como null por enquanto.
    const payload = {
      RegionalId: getCompanyIdCookie(),
      CompanyName: clientName,
      TradeName: isPF ? "" : clientTradeName,
      Doc: doc,
      RepresentativeId: representative || null,
      SellerId: salesperson || null,
      ServicePlanId: servicePlan || null,
      MasterUserEmail: null,
      MasterUserName: null,
      MasterUserAccessLevelId: null,
      StateRegistration: isPF ? "" : stateRegistration,
      MunicipalRegistration: isPF ? "" : municipalRegistration,
      SimpleTax: isSimpleTax ? "S" : "N",
      Public: null,
      Email: responsibleEmail,
      PhoneNumber: formatToE164(phone1),
      SecondaryPhoneNumber: formatToE164(phone2),
      Observation: null,
      OperationalObservation: null,
      BillingObservation: null,
      BirthDate: birthDate || null,
      PersonType: clientType,
      Dda: dda,
      CreditAnalysis: creditAnalysis,
      CreditAnalysisDate: null,
      PaymentBook: installmentBooklet,
      AdministrativeRestrictionId: restrictionType || null,
      AdministrativeRestrictionDate: restrictionStartDate || null,
      AdministrativeRestrictionEndDate: restrictionEndDate || null,
      LastTemporarySuspensionDate: null,
      AuthorizeEmail: null,
      AuthorizeSms: null,
      AuthorizeNegativeList: null,
      AuthorizeAutomaticRestriction: null,
      Whatsapp: null,
      ZipCode: mainAddress?.cep?.replace(/\D/g, "") || "",
      Street: mainAddress?.address || "",
      Number: mainAddress?.number || "",
      Neighborhood: mainAddress?.neighborhood || "",
      Complement: mainAddress?.complement || "",
      City: mainAddress?.city || "",
      State: mainAddress?.uf || "",
      SecondaryZipCode: billingAddress?.cep?.replace(/\D/g, "") || "",
      SecondaryStreet: billingAddress?.address || "",
      SecondaryNumber: billingAddress?.number || "",
      SecondaryNeighborhood: billingAddress?.neighborhood || "",
      SecondaryComplement: billingAddress?.complement || "",
      SecondaryCity: billingAddress?.city || "",
      SecondaryState: billingAddress?.uf || "",
    };
    try {
      const result = isNewCustomer
        ? await clientService.createClient(payload)
        : await clientService.updateClient(clientId, payload);

        if (!result || result.error) {
        const validationMessage = errorHandling.formatValidationErrors(result?.raw?.validations);
        const backendMessage = errorHandling.stripProcedurePrefix(
          result?.friendlyMessage || result?.message
        );
        scrollToTop();
        showToast(
          t("COMMON.ERROR"),
          validationMessage || backendMessage || t("CUSTOMER.PERSONAL_DATA.UPDATE_ERROR"),
          "Danger"
        );
        return;
      }

      showToast(t("COMMON.SUCCESS"), t("CUSTOMER.PERSONAL_DATA.UPDATED_SUCCESS"), "Success");

      const savedClientId = isNewCustomer ? result?.clientId ?? result?.ClientId : clientId;
      if (savedClientId) {
        onClientSaved?.(savedClientId, clientName);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleBillingClick = () => {
    showToast(t("COMMON.ATTENTION"), t("CUSTOMER.TAB_WIP"), "Warning");
  };

  return (
    <div className="p-3">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      {clientError && (
        <div className="mb-3">
          <small className="text-danger d-block">{t("CUSTOMER.PERSONAL_DATA.LOAD_CLIENT_ERROR")}</small>
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>{t("CUSTOMER.PERSONAL_DATA.SECTION_CLIENT_DATA")}</strong>
          <small className="text-muted">
            <span className="text-danger">*</span> {t("CUSTOMER.PERSONAL_DATA.REQUIRED_FIELDS_HINT")}
          </small>
        </div>
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.CLIENT_ID")}</label>
              <input
                type="text"
                className="form-control"
                style={DISABLED_FIELD_HEIGHT}
                value={clientId ?? ""}
                disabled
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.CLIENT_TYPE")}</label>
              <select
                className="form-select"
                value={clientType}
                onChange={(e) => {
                  const newType = e.target.value;
                  setClientType(newType);
                  if (newType === CLIENT_TYPE.PF && doc.replace(/\D/g, "").length > 11) {
                    setDoc("");
                  }
                }}
                disabled={isLoadingClient}
              >
                <option value="">{t("CUSTOMER.PERSONAL_DATA.SELECT_PLACEHOLDER")}</option>
                <option value={CLIENT_TYPE.PF}>{t("CUSTOMER.PERSONAL_DATA.CLIENT_TYPE_PF")}</option>
                <option value={CLIENT_TYPE.PJ}>{t("CUSTOMER.PERSONAL_DATA.CLIENT_TYPE_PJ")}</option>
              </select>
            </div>
            <div className="col-md-3 d-flex">
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="personalDataSimpleTax"
                  checked={isSimpleTax}
                  onChange={(e) => setIsSimpleTax(e.target.checked)}
                  disabled={isLoadingClient}
                />
                <label className="form-check-label" htmlFor="personalDataSimpleTax">
                  {t("CUSTOMER.PERSONAL_DATA.SIMPLE_TAX")}
                </label>
              </div>
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-md-3">
              <SearchInputDocument
                key={isPF ? "doc-pf" : "doc-pj"}
                name="doc"
                label={`${isPF ? "CPF" : "CNPJ"} *`}
                value={doc}
                onChange={(e) => {
                  setDoc(e.target.value.replace(/\D/g, ""));
                  if (fieldErrors.doc) setFieldErrors((prev) => ({ ...prev, doc: false }));
                }}
                disabled={isLoadingClient}
              />
              {fieldErrors.doc && (
                <div className="text-danger small mt-1">
                  {t("CUSTOMER.PERSONAL_DATA.DOC_REQUIRED")}
                </div>
              )}
            </div>
            {/* Pessoa Física não tem Nome Fantasia — aqui entra o Nome (mesmo
                slot ao lado do documento), em vez de ficar sozinho numa linha
                abaixo. Pessoa Jurídica não usa esse slot — Nome Fantasia
                fica na linha de baixo, junto da Razão Social. */}
            {isPF && (
              <div className="col-md-4">
                <label className="form-label">
                  {t("COMMON.NAME")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control${fieldErrors.clientName ? " is-invalid" : ""}`}
                  value={clientName}
                  onChange={(e) => {
                    setClientName(e.target.value);
                    if (fieldErrors.clientName) setFieldErrors((prev) => ({ ...prev, clientName: false }));
                  }}
                  disabled={isLoadingClient}
                />
                {fieldErrors.clientName && (
                  <div className="invalid-feedback">
                    {t("CUSTOMER.PERSONAL_DATA.CLIENT_NAME_REQUIRED")}
                  </div>
                )}
              </div>
            )}
          </div>

          {!isPF && (
            <div className="row g-3 mt-1">
              <div className="col-md-4">
                <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.CLIENT_TRADE_NAME")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={clientTradeName}
                  onChange={(e) => setClientTradeName(e.target.value)}
                  disabled={isLoadingClient}
                />
              </div>
            </div>
          )}

          {!isPF && (
            <div className="row g-3 mt-1">
              <div className="col-md-5">
                <label className="form-label">
                  {t("CUSTOMER.PERSONAL_DATA.CLIENT_NAME")} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control${fieldErrors.clientName ? " is-invalid" : ""}`}
                  value={clientName}
                  onChange={(e) => {
                    setClientName(e.target.value);
                    if (fieldErrors.clientName) setFieldErrors((prev) => ({ ...prev, clientName: false }));
                  }}
                  disabled={isLoadingClient}
                />
                {fieldErrors.clientName && (
                  <div className="invalid-feedback">
                    {t("CUSTOMER.PERSONAL_DATA.CLIENT_NAME_REQUIRED")}
                  </div>
                )}
              </div>
              <div className="col-md-3">
                <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.STATE_REGISTRATION")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={stateRegistration}
                  onChange={(e) => setStateRegistration(e.target.value)}
                  disabled={isLoadingClient}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.MUNICIPAL_REGISTRATION")}</label>
                <input
                  type="text"
                  className="form-control"
                  value={municipalRegistration}
                  onChange={(e) => setMunicipalRegistration(e.target.value)}
                  disabled={isLoadingClient}
                />
              </div>
            </div>
          )}

          <div className="row g-3 mt-1">
            <div className="col-md-3">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.BIRTH_DATE")}</label>
              <input
                type="date"
                className="form-control"
                value={birthDate ? birthDate.slice(0, 10) : ""}
                onChange={(e) => setBirthDate(e.target.value)}
                disabled={isLoadingClient}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.EMAIL")}</label>
              <input
                type="email"
                className="form-control"
                value={responsibleEmail}
                onChange={(e) => setResponsibleEmail(e.target.value)}
                disabled={isLoadingClient}
              />
            </div>
          </div>

          <div className="row g-3 mt-1">
            <div className="col-md-3">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.PHONE")}</label>
              <input
                type="text"
                className="form-control"
                value={formatPhoneNumber(phone1)}
                onChange={(e) => setPhone1(formatPhoneNumber(e.target.value))}
                disabled={isLoadingClient}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.CELLPHONE")}</label>
              <input
                type="text"
                className="form-control"
                value={formatPhoneNumber(phone2)}
                onChange={(e) => setPhone2(formatPhoneNumber(e.target.value))}
                disabled={isLoadingClient}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.CREATED_AT")}</label>
              <input
                type="text"
                className="form-control"
                style={DISABLED_FIELD_HEIGHT}
                value={createdAt ? new Date(createdAt).toLocaleString("pt-BR") : ""}
                disabled
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.UPDATED_AT")}</label>
              <input
                type="text"
                className="form-control"
                style={DISABLED_FIELD_HEIGHT}
                value={updatedAt ? new Date(updatedAt).toLocaleString("pt-BR") : ""}
                disabled
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <strong>{t("CUSTOMER.PERSONAL_DATA.SECTION_FINANCIAL")}</strong>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.CREDIT_ANALYSIS")}</label>
              <select
                className="form-select"
                value={creditAnalysis}
                onChange={(e) => setCreditAnalysis(e.target.value)}
                disabled={isLoadingClient}
              >
                <option value="">{t("CUSTOMER.PERSONAL_DATA.SELECT_PLACEHOLDER")}</option>
                <option value={YES_NO.YES}>{t("CUSTOMER.PERSONAL_DATA.CREDIT_ANALYSIS_APPROVED")}</option>
                <option value={YES_NO.NO}>{t("CUSTOMER.PERSONAL_DATA.CREDIT_ANALYSIS_REJECTED")}</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.DDA")}</label>
              <select
                className="form-select"
                value={dda}
                onChange={(e) => setDda(e.target.value)}
                disabled={isLoadingClient}
              >
                <option value="">{t("CUSTOMER.PERSONAL_DATA.SELECT_PLACEHOLDER")}</option>
                <option value={YES_NO.YES}>{t("COMMON.YES")}</option>
                <option value={YES_NO.NO}>{t("COMMON.NO")}</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.INSTALLMENT_BOOKLET")}</label>
              <select
                className="form-select"
                value={installmentBooklet}
                onChange={(e) => setInstallmentBooklet(e.target.value)}
                disabled={isLoadingClient}
              >
                <option value="">{t("CUSTOMER.PERSONAL_DATA.SELECT_PLACEHOLDER")}</option>
                <option value={YES_NO.YES}>{t("COMMON.YES")}</option>
                <option value={YES_NO.NO}>{t("COMMON.NO")}</option>
              </select>
            </div>
          </div>

          <div className="d-flex justify-content-end mt-3">
            <button type="button" className="btn btn-primary" onClick={handleBillingClick}>
              {t("CUSTOMER.PERSONAL_DATA.BILLING_BUTTON")}
            </button>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <strong>{t("CUSTOMER.PERSONAL_DATA.SECTION_RESTRICTION")}</strong>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.RESTRICTION_TYPE")}</label>
              {isLoadingRestrictions ? (
                <Spinner />
              ) : (
                <select
                  className="form-select"
                  value={restrictionType}
                  onChange={(e) => setRestrictionType(e.target.value)}
                  disabled={restrictionsError}
                >
                  <option value="">{t("CUSTOMER.PERSONAL_DATA.SELECT_PLACEHOLDER")}</option>
                  {restrictions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.description}
                    </option>
                  ))}
                </select>
              )}
              {restrictionsError && (
                <small className="text-danger d-block mt-1">
                  {t("CUSTOMER.PERSONAL_DATA.LOAD_RESTRICTIONS_ERROR")}
                </small>
              )}
            </div>
            <div className="col-md-3">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.RESTRICTION_START_DATE")}</label>
              <input
                type="date"
                className="form-control"
                value={restrictionStartDate ? restrictionStartDate.slice(0, 10) : ""}
                onChange={(e) => setRestrictionStartDate(e.target.value)}
                disabled={isLoadingClient}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.RESTRICTION_END_DATE")}</label>
              <input
                type="date"
                className="form-control"
                value={restrictionEndDate ? restrictionEndDate.slice(0, 10) : ""}
                onChange={(e) => setRestrictionEndDate(e.target.value)}
                disabled={isLoadingClient}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.LAST_SUSPENSION_DATE")}</label>
              <input
                type="text"
                className="form-control"
                style={DISABLED_FIELD_HEIGHT}
                value={lastSuspensionDate ? new Date(lastSuspensionDate).toLocaleDateString("pt-BR") : ""}
                disabled
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <strong>{t("CUSTOMER.PERSONAL_DATA.SECTION_COMMERCIAL")}</strong>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.PERSONAL_DATA.REPRESENTATIVE")} <span className="text-danger">*</span>
              </label>
              {isLoadingRepresentatives ? (
                <Spinner />
              ) : (
                <select
                  className={`form-select${fieldErrors.representative ? " is-invalid" : ""}`}
                  value={representative}
                  onChange={(e) => {
                    handleRepresentativeChange(e.target.value);
                    if (fieldErrors.representative) {
                      setFieldErrors((prev) => ({ ...prev, representative: false }));
                    }
                  }}
                  disabled={representativesError}
                >
                  <option value="">{t("CUSTOMER.PERSONAL_DATA.SELECT_PLACEHOLDER")}</option>
                  {representatives.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              )}
              {fieldErrors.representative && (
                <div className="invalid-feedback">
                  {t("CUSTOMER.PERSONAL_DATA.REPRESENTATIVE_REQUIRED")}
                </div>
              )}
              {representativesError && (
                <small className="text-danger d-block mt-1">
                  {t("CUSTOMER.PERSONAL_DATA.LOAD_REPRESENTATIVES_ERROR")}
                </small>
              )}
            </div>
            <div className="col-md-4">
              <label className="form-label">{t("CUSTOMER.PERSONAL_DATA.COMMERCIAL")}</label>
              {isLoadingSalespeople ? (
                <Spinner />
              ) : (
                <select
                  className="form-select"
                  value={salesperson}
                  onChange={(e) => setSalesperson(e.target.value)}
                  disabled={salespeopleError || !representative}
                >
                  <option value="">{t("CUSTOMER.PERSONAL_DATA.SELECT_PLACEHOLDER")}</option>
                  {salespeople.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              )}
              {salespeopleError && (
                <small className="text-danger d-block mt-1">
                  {t("CUSTOMER.PERSONAL_DATA.LOAD_SALESPEOPLE_ERROR")}
                </small>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <strong>{t("CUSTOMER.PERSONAL_DATA.SECTION_SERVICE_PLAN")}</strong>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">
                {t("CUSTOMER.PERSONAL_DATA.SERVICE_PLAN")} <span className="text-danger">*</span>
              </label>
              {isLoadingPlans ? (
                <Spinner />
              ) : (
                <select
                  className={`form-select${fieldErrors.servicePlan ? " is-invalid" : ""}`}
                  value={servicePlan}
                  onChange={(e) => {
                    setServicePlan(e.target.value);
                    if (fieldErrors.servicePlan) {
                      setFieldErrors((prev) => ({ ...prev, servicePlan: false }));
                    }
                  }}
                  disabled={plansError}
                >
                  <option value="">{t("CUSTOMER.PERSONAL_DATA.SELECT_PLACEHOLDER")}</option>
                  {plans.map((plan) => (
                    <option key={plan.servicePlanId} value={plan.servicePlanId}>
                      {plan.servicePlanName}
                    </option>
                  ))}
                </select>
              )}
              {fieldErrors.servicePlan && (
                <div className="invalid-feedback">
                  {t("CUSTOMER.PERSONAL_DATA.SERVICE_PLAN_REQUIRED")}
                </div>
              )}
              {plansError && (
                <small className="text-danger d-block mt-1">
                  {t("CUSTOMER.PERSONAL_DATA.LOAD_PLANS_ERROR")}
                </small>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? t("CUSTOMER.PERSONAL_DATA.UPDATING") : t("COMMON.SAVE")}
        </button>
      </div>
    </div>
  );
}
